import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { newsArticles } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 6;
const FILTERS = ["全部", "影评", "报道", "发布"] as const;

export default function NewsList() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("全部");
  const [page, setPage] = useState(1);

  useSeo({
    title: "久久爱-影视资讯与影评文章，行业动态及时掌握",
    description:
      "久久爱影视资讯页汇集最新行业报道、深度影评与官方发布资讯，涵盖票房动态、剧集官宣与创作趋势，帮你及时了解影视圈的最新风向。",
    keywords: "久久爱,影视资讯,影评,行业报道,影视新闻,票房动态",
  });

  const sorted = useMemo(
    () => [...newsArticles].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt)),
    []
  );

  const filtered = useMemo(
    () => (filter === "全部" ? sorted : sorted.filter((a) => a.category === filter)),
    [filter, sorted]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const changeFilter = (value: (typeof FILTERS)[number]) => {
    setFilter(value);
    setPage(1);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb items={[{ label: "影视资讯" }]} />
            <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-4xl font-heading">
              影视资讯
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              行业动态、深度影评与最新发布资讯，用更专业的视角，带你读懂影视作品与市场风向。
            </p>
          </div>
        </div>

        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-wrap items-center gap-2">
            {FILTERS.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => changeFilter(item)}
                className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                  filter === item
                    ? "border-primary-500 bg-primary-500 text-background-50"
                    : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-foreground-900"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          {pageItems.length === 0 ? (
            <div className="mt-8 flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-newspaper-line text-3xl"></i>
              </span>
              <h3 className="text-base font-semibold text-foreground-800">
                暂无该分类的资讯内容
              </h3>
              <p className="text-sm text-foreground-500">换个分类看看吧</p>
              <button
                type="button"
                onClick={() => changeFilter("全部")}
                className="mt-2 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部资讯
              </button>
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-4">
              {pageItems.map((article) => (
                <Link
                  key={article.id}
                  to={`/news/${article.id}`}
                  className="group flex cursor-pointer flex-col gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3 transition-all duration-300 hover:border-primary-200 hover:bg-background-100 md:flex-row md:items-center md:p-4"
                >
                  <div className="h-44 w-full shrink-0 overflow-hidden rounded-md bg-background-200 md:h-32 md:w-56">
                    <img
                      src={article.cover}
                      alt={`${article.title}资讯配图`}
                      title={`${article.title} - 久久爱影视资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <span className="rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-900">
                        {article.category}
                      </span>
                      <span className="text-foreground-400">{article.publishedAt}</span>
                      <span className="text-foreground-400">{article.author}</span>
                    </div>
                    <h2 className="mt-2 line-clamp-2 text-base font-bold text-foreground-950 md:text-lg">
                      {article.title}
                    </h2>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                      {article.summary}
                    </p>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-700">
                      阅读全文
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <button
                type="button"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-3 py-2 text-sm transition-colors ${
                  page === 1
                    ? "cursor-not-allowed border-background-200 text-foreground-400"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                <i className="ri-arrow-left-s-line text-base"></i>
                上一页
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => setPage(num)}
                  className={`h-9 w-9 cursor-pointer rounded-md text-sm font-medium transition-colors ${
                    num === page
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {num}
                </button>
              ))}
              <button
                type="button"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border px-3 py-2 text-sm transition-colors ${
                  page === totalPages
                    ? "cursor-not-allowed border-background-200 text-foreground-400"
                    : "border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                下一页
                <i className="ri-arrow-right-s-line text-base"></i>
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}